// web/src/hooks/useSelectionManager.js
import { useState, useCallback, useEffect } from "react";
import { FeatureTypeConfig } from "../components/dataCatalog/config/featureTypes";

export const useSelectionManager = (initialParameters = {}, isOpen) => {
  // `= {}` only covers `undefined` -- DataCatalog.js passes `state.search.parameters` straight
  // through, which is explicitly `null` (not undefined) whenever no search has parsed
  // successfully yet, so it bypasses that default entirely without this.
  const [selections, setSelections] = useState(initialParameters ?? {});

  // Real bug report: clearing the search bar (resetParameters) left a Data Catalog reopened
  // afterward still showing the previous selection. DataCatalog.js renders this hook's owner
  // unconditionally (only the antd Modal's own `open` prop toggles visibility, and its
  // `destroyOnClose` only tears down the Modal's own children, never this component), so the
  // `useState` above only ever reads `initialParameters` once, on the very first mount -- never
  // again for the lifetime of the search bar. Re-deriving on every open (not on every
  // `initialParameters` change, which would also wipe unsaved in-progress edits while the
  // catalog is open) is what actually makes "reopen the catalog" mean "start from what's
  // currently in the bar."
  useEffect(() => {
    if (isOpen) setSelections(initialParameters ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately keyed on `isOpen`
    // alone; see comment above.
  }, [isOpen]);

  const addSelection = useCallback((item, type) => {
    setSelections((prev) => {
      const newSelections = { ...prev };

      switch (type) {
        case "metrics":
          const metricConfig = FeatureTypeConfig[item.type];
          if (!metricConfig) {
            console.error(
              `No configuration found for metric type: ${item.type}`
            );
            return prev;
          }
          newSelections.metrics = {
            metrics: metricConfig.handleMetricSelection(
              prev.metrics?.metrics || [],
              item
            ),
          };
          break;
        case "filters":
          const filterConfig = FeatureTypeConfig.filter;
          if (!filterConfig) {
            console.error("No configuration found for filter type");
            return prev;
          }
          newSelections.filters = {
            filters: filterConfig.handleFilterSelection(
              prev.filters?.filters || [],
              item
            ),
            needs_filtering: true,
          };
          break;
        case "places":
          const placeConfig = FeatureTypeConfig.places;
          if (!placeConfig) {
            console.error("No configuration found for places type");
            return prev;
          }
          newSelections.places = placeConfig.handlePlaceSelection(
            prev.places || [],
            item
          );
          break;
        default:
          console.error(`Unknown selection type: ${type}`);
          return prev;
      }

      return newSelections;
    });
  }, []);

  const removeSelection = useCallback((item, type) => {
    setSelections((prev) => {
      const newSelections = { ...prev };

      switch (type) {
        case "metrics":
          const metricConfig = FeatureTypeConfig[item.type];
          if (!metricConfig) {
            console.error(
              `No configuration found for metric type: ${item.type}`
            );
            return prev;
          }
          newSelections.metrics = {
            metrics: metricConfig.handleMetricRemoval(
              prev.metrics?.metrics || [],
              item
            ),
          };
          break;
        case "filters":
          const filterConfig = FeatureTypeConfig.filter;
          if (!filterConfig) {
            console.error("No configuration found for filter type");
            return prev;
          }
          const remainingFilters = filterConfig.handleFilterRemoval(
            prev.filters?.filters || [],
            item
          );

          newSelections.filters = {
            filters: remainingFilters,
            needs_filtering: remainingFilters.length > 0,
          };
          break;
        case "places":
          const placeConfig = FeatureTypeConfig.places;
          if (!placeConfig) {
            console.error("No configuration found for places type");
            return prev;
          }
          newSelections.places = placeConfig.handlePlaceRemoval(
            prev.places || [],
            item
          );
          break;
        default:
          console.error(`Unknown selection type: ${type}`);
          return prev;
      }

      return newSelections;
    });
  }, []);

  const isSelected = useCallback(
    (item, type) => {
      switch (type) {
        case "metrics":
          const metricConfig = FeatureTypeConfig[item.type];
          if (!metricConfig) {
            console.error(
              `No configuration found for metric type: ${item.type}`
            );
            return false;
          }
          return metricConfig.isSelected(selections[type], item);
        case "filters":
          const filterConfig = FeatureTypeConfig.filter;
          if (!filterConfig) {
            console.error("No configuration found for filter type");
            return false;
          }
          return filterConfig.isSelected(selections[type], item);
        case "places":
          const placeConfig = FeatureTypeConfig.places;
          if (!placeConfig) {
            console.error("No configuration found for places type");
            return false;
          }
          return placeConfig.isSelected(selections[type], item);
        default:
          console.error(`Unknown selection type: ${type}`);
          return false;
      }
    },
    [selections]
  );

  return {
    selections,
    addSelection,
    removeSelection,
    isSelected,
  };
};
